import * as Yup from "yup";

Yup.addMethod<Yup.DateSchema>(
    Yup.string,
    "unique",
    function (message: string, list: any[]) {
        return this.test("unique", message, function (value) {
            if (list.filter(item => item == value).length > 1) {
                return false;
            }

            return true;
        });
    }
);

const generateYupValidator = config =>
    Yup.object().shape(
        Object.keys(config).reduce((accumulator, configkey) => {
            if (config[configkey].validation) {
                return {
                    [configkey]: config[configkey].validation,
                    ...accumulator
                };
            }

            return accumulator;
        }, {})
    );

const validatorsMap = {
    string: validator => Yup.string().nullable(),
    number: validator => Yup.number().nullable(),
    integer: validator => Yup.number().integer("Must be an integer").nullable(),
    array: validator => Yup.array(),
    boolean: validator => Yup.boolean(),
    minLength: (validator, minLen: number) =>
        validator.min(minLen, `Min. ${minLen} character(s).`),
    required: validator => {
        return validator.required("Field is required.");
    },
    maxLength: (validator, maxLen) =>
        (validator = validator.max(maxLen, `Max. ${maxLen} character(s).`)),
    pattern: (validator, pattern) =>
        validator.matches(new RegExp(pattern), `Invalid value.`),
    maximum: (validator, maxValue) =>
        validator.max(maxValue, `Must be <= than ${maxValue}`),
    minimum: (validator, minValue) =>
        validator.min(minValue, `Must be >= than ${minValue}`),
    exclusiveMinimum: (validator, excMin) =>
        validator.moreThan(excMin, `Must be > than ${excMin}`),
    lte: (validator, field, defaultValue = 4294967295) =>
        validator.when(field.key, (limit, schema) => {
            return limit
                ? schema.max(limit, `Must be <= than ${field.label}`)
                : schema.max(defaultValue, `Must be <= than ${defaultValue}`);
        }),
    gte: (validator, field, defaultValue = 0) =>
        validator.when(field.key, (limit, schema) =>
            limit
                ? schema.min(limit, `Must be >= than ${field.label}`)
                : schema.min(defaultValue, `Must be >= than ${defaultValue}`)
        ),
    gt: (validator, field, defaultValue = 0) =>
        validator.when(field.key, (limit, schema) =>
            limit ? schema.moreThan(limit) : schema.moreThan(defaultValue)
        ),
    lt: (validator, field, defaultValue = 4294967295) =>
        validator.when(field.key, (limit, schema) =>
            limit ? schema.lessThan(limit) : schema.lessThan(defaultValue)
        ),
    unique: (validator, list) =>
        validator.unique("Field value must be unique.", list),
    email: () => Yup.string().email("e-mail is not valid")
};

const generateYupValidatorFromSchema = validationSchema => {
    let validator;

    let validationFieldSchema = { ...validationSchema };
    if (
        validationFieldSchema.hasOwnProperty("type") &&
        Array.isArray(validationFieldSchema.type)
    ) {
        const newType = validationFieldSchema.type.find(
            type => type !== null && type !== "null" && type !== undefined
        );

        if (newType) {
            validationFieldSchema.type = newType;
        }
    }

    if (!validatorsMap.hasOwnProperty(validationFieldSchema.type)) {
        return undefined;
    }

    if (validationFieldSchema.type) {
        if (validatorsMap[validationFieldSchema.type]) {
            validator = validatorsMap[validationFieldSchema.type](validator);
        }
    }

    const isConvert = validationFieldSchema.hasOwnProperty("convert") && validationFieldSchema.convert;
    for (let att in validationFieldSchema) {
        if (
            att !== "type" &&
            att !== "convert" &&
            att !== "lessThan" &&
            att !== "exclusiveLessThan" &&
            att !== "moreThan" &&
            att !== "exclusiveMoreThan" &&
            validatorsMap.hasOwnProperty(att) &&
            !!att
        ) {
            if (
                att !== "required" ||
                (att === "required" && validationFieldSchema[att] !== false)
            ) {
                if (
                    (att == "maximum" ||
                        att == "minimum" ||
                        att == "exclusiveMinimum") &&
                    isConvert
                ) {
                    validator = validatorsMap[att](
                        validator,
                        validationFieldSchema[att] / 100
                    );
                } else if (att == "lte" || att == "lt") {
                    validator = validatorsMap[att](
                        validator,
                        validationFieldSchema[att],
                        validationFieldSchema.hasOwnProperty("maximum")
                            ? validationFieldSchema["maximum"]
                            : validationFieldSchema.hasOwnProperty("lessThan")
                            ? validationFieldSchema["lessThan"]
                            : undefined
                    );
                } else if (att == "gte" || att == "gt") {
                    validator = validatorsMap[att](
                        validator,
                        validationFieldSchema[att],
                        validationFieldSchema.hasOwnProperty("minimum")
                            ? validationFieldSchema["minimum"]
                            : validationFieldSchema.hasOwnProperty("moreThan")
                            ? validationFieldSchema["moreThan"]
                            : undefined
                    );
                } else {
                    validator = validatorsMap[att](
                        validator,
                        validationFieldSchema[att]
                    );
                }
            }
        }
    }

    return validator;
};

const areFieldsDifferent = (fieldsA, fieldsB) => {
    if (fieldsA.length !== fieldsB.length) return true;

    for (let i = 0; i < fieldsA.length; i++) {
        const fieldA = fieldsA[i];
        const fieldB = fieldsB[i];

        if (fieldA.fieldType !== fieldB.fieldType) return true;

        if (
            fieldA.fieldType === "twoPanelSelector" ||
            fieldA.fieldType == "bulletsSelector"
        ) {
            return !(
                fieldA.fieldOptions.items.length ===
                    fieldB.fieldOptions.items.length &&
                fieldA.fieldOptions.items.every(item =>
                    fieldB.fieldOptions.items.includes(item)
                )
            );
        }

        if (
            fieldA.fieldType == fieldB.fieldType &&
            fieldA.name != fieldB.name
        ) {
            return true;
        }

        if (fieldA.fieldType === "formSetWays") {
            return areFieldsDifferent(fieldA.fieldOptions.subFormTypes[0].fields,fieldB.fieldOptions.subFormTypes[0].fields)
        }
    }

    return false;
};

export const FormUtils = {
    generateYupValidator,
    generateYupValidatorFromSchema,
    areFieldsDifferent
};
